import i18next from 'i18next'
import ChainedBackend from 'i18next-chained-backend'
import HttpApi from 'i18next-http-backend'
import { initReactI18next } from 'react-i18next'

import { dataLayerPublish } from '@dhl/datalayer'
import { customDetectors, languageDetector } from '@dhl/i18n'
import { trackTraceTranslationsI18nBackend } from '@dhl/tracktrace'
import { useLocalStorage } from '@dhl/use-storage'

import { forTopLevelDomain, getConfig } from '~/config'

// Content hashed translation files per locale, only defined in production builds (HashedTranslationsPlugin)
// eslint-disable-next-line no-underscore-dangle, @typescript-eslint/naming-convention -- injected by the DefinePlugin
declare const __TRANSLATION_FILES__: Record<string, string> | undefined
const translationFiles = typeof __TRANSLATION_FILES__ === 'undefined' ? undefined : __TRANSLATION_FILES__

const { apiGatewayHost } = getConfig()

export const localStorageLocale = 'preferred-locale'

// Because i18next does not keep the locales in its own state, we also export
// this function to get the locales in the language selector
export const locales = () =>
  forTopLevelDomain({
    be: ['en-BE', 'fr-BE', 'nl-BE'],
    nl: ['en-NL', 'nl-NL'],
  })

export const useLocalStorageForLocale = () => useLocalStorage<string>(localStorageLocale, undefined, { raw: true })

i18next
  .use(languageDetector(locales))
  .use(initReactI18next) // passes i18n down to react-i18next
  .use(ChainedBackend)
  .init({
    backend: {
      backends: [HttpApi, trackTraceTranslationsI18nBackend({ apiGatewayHost })],
      backendOptions: [
        {
          loadPath: (lngs: string[], ns: string[]) => {
            if (ns.includes('translation')) {
              const hashedFile = translationFiles?.[lngs[0]]

              return hashedFile ? `/home/${hashedFile}` : `/home/i18n/{{lng}}.json`
            }
            return undefined
          },
        },
      ],
    },
    ns: ['translation', 'trackTrace'],
    defaultNS: 'translation',
    fallbackNS: 'trackTrace',
    saveMissing: true,
    showSupportNotice: false,
    missingKeyHandler: (_lngs, _ns, key) => {
      dataLayerPublish('ERROR', {
        id: 'i18n',
        message: `i18n - missing '${i18next.language}' translation for key '${key}'`,
      })
    },
    debug: false,
    supportedLngs: locales(),
    returnNull: false,
    detection: {
      order: [customDetectors.queryString, 'localStorage', customDetectors.detectPrimary],
      caches: [],
      lookupQuerystring: 'lang',
      lookupLocalStorage: localStorageLocale,
    },
    interpolation: {
      escapeValue: false, // React already prevents XSS
    },
    react: {
      bindI18nStore: 'added',
    },
  })

export const i18n = i18next
